/**
 * Desktop OAuth token store — OS keychain (Rust ``keyring``) when in Tauri,
 * in-memory fallback in dev / web smoke contexts.
 *
 * Tokens we persist:
 *   - ``access_token``  — Bearer header on every API call (short-lived,
 *                          15 min default on Zitadel).
 *   - ``refresh_token`` — rotated on every ``/auth/desktop/refresh``;
 *                          MUST overwrite the previous secret atomically.
 *   - ``expires_at``    — epoch ms; the silent-refresh scheduler reads
 *                          this to decide when to rotate.
 *   - ``user_info``     — projection of the ``id_token`` claims (avatar
 *                          + org pill bootstrap, before ``/auth/me``).
 *
 * Why a per-process singleton (instead of just reading from the
 * ``packages/core/storage`` secrets vault on every call):
 *   - ``ApiClient.setAuthTokenProvider`` is invoked once at boot — we
 *     need a synchronous-looking ``read()`` from a hot path.
 *   - The vault read is async; caching in memory keeps the request
 *     overhead at zero while we still write through to the persistent
 *     store on every mutation.
 */

import type { DesktopUserInfo } from "@ando/core";
import { getSecretStorage } from "@ando/core/storage";

const VAULT_KEY = "ando.desktop.tokens.v1";

export interface DesktopTokenState {
  readonly access_token: string;
  readonly refresh_token: string | null;
  readonly id_token: string | null;
  readonly expires_at: number;
  readonly user_info: DesktopUserInfo;
}

let cache: DesktopTokenState | null = null;
let hydrated = false;

async function hydrateFromVault(): Promise<DesktopTokenState | null> {
  if (hydrated) return cache;
  try {
    const persisted = await getSecretStorage().get<DesktopTokenState>(VAULT_KEY);
    cache = persisted ?? null;
  } catch {
    cache = null;
  } finally {
    hydrated = true;
  }
  return cache;
}

export async function loadDesktopTokens(): Promise<DesktopTokenState | null> {
  return cache ?? hydrateFromVault();
}

/**
 * Synchronous view of the in-memory token cache — ``null`` both when the
 * user is signed out AND when the vault has not been hydrated yet. Route
 * guards call ``loadDesktopTokens()`` (async) once, then read this on every
 * navigation; the OS keychain is never touched on the hot path.
 */
export function peekDesktopTokens(): DesktopTokenState | null {
  return cache;
}

export async function saveDesktopTokens(state: DesktopTokenState): Promise<void> {
  cache = state;
  hydrated = true;
  try {
    await getSecretStorage().set(VAULT_KEY, state);
  } catch {
    // Persist best-effort — the in-memory cache keeps the session
    // alive for the current process even if the vault write fails.
  }
}

export async function clearDesktopTokens(): Promise<void> {
  cache = null;
  hydrated = true;
  try {
    await getSecretStorage().remove(VAULT_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Synchronous-ish read for the ``ApiClient`` auth-token provider.
 *
 * Returns the access token if the in-memory cache has one; ``null``
 * otherwise. The first request after boot triggers a vault hydrate
 * (the provider awaits this); subsequent calls hit the cache.
 */
export async function readAccessToken(): Promise<string | null> {
  const state = cache ?? (await hydrateFromVault());
  if (!state) return null;
  return state.access_token;
}

export async function readRefreshToken(): Promise<string | null> {
  const state = cache ?? (await hydrateFromVault());
  if (!state) return null;
  return state.refresh_token;
}

export async function readUserInfo(): Promise<DesktopUserInfo | null> {
  const state = cache ?? (await hydrateFromVault());
  if (!state) return null;
  return state.user_info;
}

/** Computed: 30s safety margin so the request body isn't sent with a
 * token that expires mid-flight. */
export function isAccessTokenExpired(state: DesktopTokenState): boolean {
  return Date.now() + 30_000 >= state.expires_at;
}
